import type { CategoryRef, PostNode } from "@/app/components/PostGrid";

export type ProgrammeLevel = "upper-sec" | "h2";

export type ProgrammePost = PostNode & {
  subject?: string | null;
};

export type ProgrammePostGroupInput = {
  subject: string;
  title: string;
  description?: string;
  href?: string;
};

export type ProgrammePostGroupResolved = ProgrammePostGroupInput & {
  posts: ProgrammePost[];
};

export type NotesResourcePostGroup = {
  id: "topic-notes" | "study-guides";
  title: string;
  posts: ProgrammePost[];
};

export function getGroupHeaderTheme(subject: string): {
  container: string;
  title: string;
} {
  const normalized = subject.toLowerCase();

  if (normalized.includes("physics")) {
    return {
      container: "border-blue-200/70 bg-white/90",
      title: "text-blue-700",
    };
  }
  if (normalized.includes("chemistry")) {
    return {
      container: "border-emerald-200/70 bg-white/90",
      title: "text-emerald-700",
    };
  }
  if (normalized.includes("biology")) {
    return {
      container: "border-lime-200/70 bg-white/90",
      title: "text-lime-800",
    };
  }
  if (normalized.includes("maths")) {
    return {
      container: "border-amber-200/70 bg-white/90",
      title: "text-amber-700",
    };
  }
  return {
    container: "border-slate-200 bg-white/90",
    title: "text-slate-900",
  };
}

const ROMAN_NUMERALS: Record<string, number> = {
  I: 1,
  II: 2,
  III: 3,
  IV: 4,
  V: 5,
  VI: 6,
  VII: 7,
  VIII: 8,
  IX: 9,
  X: 10,
};

/**
 * Sort priority for category filter pills.
 *
 * Returns a tuple [bucket, order] where:
 *   bucket 0 = "Section I" through "Section X" syllabus sections,
 *     ordered by roman numeral
 *   bucket 1 = everything else, ordered alphabetically by label
 *
 * This keeps syllabus sections grouped and in curriculum order at the
 * left, with content-type categories (Study Tips, Exam Papers, etc.)
 * trailing in a predictable order.
 */
function getCategorySortKey(label: string): [number, number, string] {
  const sectionMatch = label.match(/^Section\s+([IVX]+)\b/i);
  if (sectionMatch) {
    const roman = ROMAN_NUMERALS[sectionMatch[1].toUpperCase()] ?? 99;
    return [0, roman, label];
  }
  return [1, 0, label.toLowerCase()];
}

export function buildProgrammeCategories({
  posts,
  showCategories,
}: {
  posts: ProgrammePost[];
  showCategories: boolean;
}): CategoryRef[] {
  if (!showCategories) return [];

  const unique = Array.from(
    new Map(
      posts
        .filter(
          (
            post
          ): post is ProgrammePost & {
            category: NonNullable<ProgrammePost["category"]>;
          } => Boolean(post.category)
        )
        .map((post) => [post.category.slug, post.category] as const)
    ).values()
  );

  return unique.sort((a, b) => {
    const [aBucket, aOrder, aLabel] = getCategorySortKey(a.label);
    const [bBucket, bOrder, bLabel] = getCategorySortKey(b.label);
    if (aBucket !== bBucket) return aBucket - bBucket;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return aLabel.localeCompare(bLabel);
  });
}

/**
 * Strip the "Section X: " prefix from a syllabus-section label so the
 * filter pill shows just the section name ("Mechanics" instead of
 * "Section II: Mechanics"). Leaves non-section labels untouched.
 */
export function getCategoryDisplayLabel(label: string): string {
  const match = label.match(/^Section\s+[IVX]+:\s*(.+)$/i);
  return match ? match[1] : label;
}

/**
 * True when the category is a syllabus section (Section I through X).
 * Used to visually group section pills separately from content types.
 */
export function isSyllabusSectionCategory(label: string): boolean {
  return /^Section\s+[IVX]+\b/i.test(label);
}

export function deriveProgrammeLevelFacet({
  posts,
  showLevelFacet,
}: {
  posts: ProgrammePost[];
  showLevelFacet: boolean;
}): {
  hasUpperSec: boolean;
  hasH2: boolean;
  hasLevelFacet: boolean;
} {
  const hasUpperSec = posts.some((post) => post.level === "upper-sec");
  const hasH2 = posts.some((post) => post.level === "h2");
  const hasLevelFacet = showLevelFacet && (hasUpperSec || hasH2);

  return { hasUpperSec, hasH2, hasLevelFacet };
}

export function resolveInitialProgrammeCategory({
  initialCategory,
  categories,
  showCategories,
}: {
  initialCategory?: string | null;
  categories: CategoryRef[];
  showCategories: boolean;
}): string | undefined {
  if (!showCategories || !initialCategory) return undefined;
  return categories.some((category) => category.slug === initialCategory)
    ? initialCategory
    : undefined;
}

export function resolveProgrammeCategoryFromParam({
  categoryParam,
  categories,
  showCategories,
}: {
  categoryParam?: string | null;
  categories: CategoryRef[];
  showCategories: boolean;
}): string | undefined {
  if (!showCategories || !categoryParam) return undefined;
  return categories.some((category) => category.slug === categoryParam)
    ? categoryParam
    : undefined;
}

export function resolveInitialProgrammeLevel({
  initialLevel,
  hasLevelFacet,
}: {
  initialLevel?: ProgrammeLevel | null;
  hasLevelFacet: boolean;
}): ProgrammeLevel | undefined {
  if (!hasLevelFacet) return undefined;
  return initialLevel === "upper-sec" || initialLevel === "h2"
    ? initialLevel
    : undefined;
}

export function resolveProgrammeLevelFromParam({
  levelParam,
  hasLevelFacet,
}: {
  levelParam?: string | null;
  hasLevelFacet: boolean;
}): ProgrammeLevel | undefined {
  if (!hasLevelFacet) return undefined;
  return levelParam === "upper-sec" || levelParam === "h2"
    ? levelParam
    : undefined;
}

export function filterProgrammePosts({
  posts,
  activeCategory,
  activeLevel,
  showCategories,
}: {
  posts: ProgrammePost[];
  activeCategory?: string;
  activeLevel?: ProgrammeLevel;
  showCategories: boolean;
}): ProgrammePost[] {
  let result = posts;

  if (activeLevel) {
    result = result.filter((post) => {
      if (!post.level) return false;
      if (post.level === "mixed") return true;
      return post.level === activeLevel;
    });
  }

  if (showCategories && activeCategory) {
    result = result.filter((post) => post.category?.slug === activeCategory);
  }

  return result;
}

const SUPPORTING_NOTES_CATEGORY_SLUGS = new Set([
  "exam-format",
  "guides",
  "study-strategy",
  "study-tips",
]);

const TOPIC_NOTE_FILENAME_PATTERNS = [
  /^h2-(?:biology|chemistry|maths|physics)-(?:core|notes|topic)-/i,
  /^ip-(?:amaths|emaths)-notes-\d/i,
  /^ip-combined-science-lower-sec-\d/i,
  /^ip-maths-lower-sec-\d/i,
  /^ip-physics-notes-\d/i,
];

function isTopicNoteCategorySlug(slug: string): boolean {
  if (!slug) return false;
  if (SUPPORTING_NOTES_CATEGORY_SLUGS.has(slug)) return false;
  if (slug.endsWith("-exam-papers")) return false;
  return slug.startsWith("h2-") || slug.startsWith("ip-");
}

function isTopicNoteFilename(filename: string): boolean {
  return TOPIC_NOTE_FILENAME_PATTERNS.some((pattern) => pattern.test(filename));
}

export function isProgrammeTopicNote(post: ProgrammePost): boolean {
  const categorySlug = post.category?.slug?.toLowerCase() ?? "";
  if (isTopicNoteCategorySlug(categorySlug)) return true;
  return categorySlug === "notes" && isTopicNoteFilename(post._sys.filename);
}

export function getProgrammeTopicNumberLabel(
  post: ProgrammePost
): string | null {
  const sources = [post._sys.filename, post.title ?? ""];

  for (const source of sources) {
    const normalized = source.trim();
    const leadingNumberMatch = normalized.match(/^(\d{1,2})(?:[-\s]|$)/);
    if (leadingNumberMatch) {
      const value = Number(leadingNumberMatch[1]);
      if (value > 0) return String(value);
    }

    const topicMatch = normalized.match(/\bTopic[-\s]+(\d{1,2})\b/i);
    if (topicMatch) return String(Number(topicMatch[1]));

    const notesMatch = normalized.match(
      /\bNotes[-\s]+(\d{1,2})(?:[-\s]+(\d{1,2}))?\b/i
    );
    if (notesMatch) {
      const major = Number(notesMatch[1]);
      if (major === 0) return null;
      return notesMatch[2]
        ? `${major}.${Number(notesMatch[2])}`
        : String(major);
    }

    const subjectMatch = normalized.match(
      /\b(?:Lower-Sec|Upper-Sec|Sec)[-\s]+(\d{1,2})\b/i
    );
    if (subjectMatch) {
      const value = Number(subjectMatch[1]);
      if (value === 0) return null;
      return String(value);
    }
  }

  return null;
}

export function buildNotesResourcePostGroups({
  posts,
}: {
  posts: ProgrammePost[];
}): NotesResourcePostGroup[] {
  const topicNotes = posts.filter(isProgrammeTopicNote).toSorted((a, b) => {
    const aLabel = getProgrammeTopicNumberLabel(a);
    const bLabel = getProgrammeTopicNumberLabel(b);

    if (aLabel && bLabel) {
      const aParts = aLabel.split(".").map(Number);
      const bParts = bLabel.split(".").map(Number);
      const partCount = Math.max(aParts.length, bParts.length);
      for (let index = 0; index < partCount; index += 1) {
        const difference = (aParts[index] ?? 0) - (bParts[index] ?? 0);
        if (difference !== 0) return difference;
      }
    }

    if (aLabel) return -1;
    if (bLabel) return 1;
    return 0;
  });
  const studyGuides = posts.filter((post) => !isProgrammeTopicNote(post));

  return [
    {
      id: "topic-notes" as const,
      title: "Topic notes",
      posts: topicNotes,
    },
    {
      id: "study-guides" as const,
      title: "Study guides",
      posts: studyGuides,
    },
  ].filter((group) => group.posts.length > 0);
}

export function buildGroupedProgrammePosts({
  posts,
  postGroups,
}: {
  posts: ProgrammePost[];
  postGroups?: ReadonlyArray<ProgrammePostGroupInput>;
}): ProgrammePostGroupResolved[] | null {
  if (!Array.isArray(postGroups) || postGroups.length === 0) return null;

  const groupedSlugs = new Set<string>();
  const groups = postGroups
    .map((group) => {
      const groupPosts = posts.filter((post) => post.subject === group.subject);
      groupPosts.forEach((post) => groupedSlugs.add(post._sys.filename));
      return { ...group, posts: groupPosts };
    })
    .filter((group) => group.posts.length > 0);

  const ungrouped = posts.filter(
    (post) => !groupedSlugs.has(post._sys.filename)
  );
  if (ungrouped.length > 0) {
    groups.push({
      subject: "__other__",
      title: "More resources",
      description: "Additional posts not tied to a specific track yet.",
      posts: ungrouped,
    });
  }

  return groups;
}

export function shouldRenderGroupedProgrammePosts({
  groupedPosts,
  activeCategory,
  activeLevel,
}: {
  groupedPosts: ProgrammePostGroupResolved[] | null;
  activeCategory?: string;
  activeLevel?: ProgrammeLevel;
}): boolean {
  return Boolean(groupedPosts) && !activeCategory && !activeLevel;
}

export function buildProgrammeFilterTarget({
  pathname,
  currentSearchParams,
  key,
  value,
}: {
  pathname: string;
  currentSearchParams: string;
  key: "category" | "level";
  value?: string;
}): string {
  const params = new URLSearchParams(currentSearchParams);
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
  const query = params.toString();
  return query.length > 0 ? `${pathname}?${query}` : pathname;
}
